import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Overlay } from '@myphx-engineering/phx-design-system';
import {
    type GetAuthTokenFromCodeProps,
    getAuthTokensFromCode,
} from '@phx/front-end-auth';

import { GenericError } from '../../components/generic-error/GenericError';
import { telemetryInstance } from '../../instrumentation';

// Send the details to telemetry only. The user sees the generic error page,
// because these messages are not written for users.
const reportCallbackError = (error: unknown) => {
    telemetryInstance.logError({
        source: window.location.pathname,
        exception: error instanceof Error ? error : new Error(String(error)),
    });
};

const InitiateTokenCallback = () => {
    const [searchParams] = useSearchParams();
    const [hasError, setHasError] = useState(false);
    const navigate = useNavigate();
    const isGenerateTokenInitiated = useRef(false);

    const redirectUri = import.meta.env.VITE_REDIRECT_URI;
    const ssoClientId = import.meta.env.VITE_TOKEN_CLIENT_ID;
    const ssoTokenApi = import.meta.env.VITE_TOKEN_API_URL;

    useLayoutEffect(() => {
        async function fetchToken() {
            const successCode = searchParams.get('code');
            const base64StateInUrl = searchParams.get('state');
            const authError = searchParams.get('error');
            const authErrorDescription = searchParams.get('error_description');

            // The code and state are single use. Remove them from the URL
            // before the token request starts, so a reload or the back button
            // cannot send the same code again. Keep history.state, because
            // React Router stores its own keys there.
            window.history.replaceState(
                window.history.state,
                '',
                `${window.location.pathname}${window.location.hash}`
            );

            if (successCode && base64StateInUrl) {
                const authTokenParams: GetAuthTokenFromCodeProps = {
                    responseCode: successCode,
                    base64StateInUrl,
                    ssoClientId,
                    redirectUri,
                    ssoTokenApi,
                };

                const { error, errorDescription, nextUrl } =
                    await getAuthTokensFromCode(authTokenParams);
                if (nextUrl) {
                    navigate(nextUrl, { replace: true });
                }

                if (error) {
                    reportCallbackError(
                        `Token exchange failed: ${error} ${errorDescription ?? ''}`.trim()
                    );
                    setHasError(true);
                }
            } else if (authError) {
                reportCallbackError(
                    `Authorization failed: ${authError} ${authErrorDescription ?? ''}`.trim()
                );
                setHasError(true);
            } else {
                reportCallbackError('Token callback has no code or state');
                setHasError(true);
            }
        }

        if (!isGenerateTokenInitiated.current) {
            isGenerateTokenInitiated.current = true;
            // The code is gone from the URL, so a reload cannot retry. Show
            // the error page instead of leaving the user on the loader.
            fetchToken().catch((error: unknown) => {
                reportCallbackError(error);
                setHasError(true);
            });
        }
    }, [navigate, ssoTokenApi, searchParams, redirectUri, ssoClientId]);

    if (hasError) {
        return <GenericError />;
    }
    return <Overlay visible loader fullScreen />;
};

export default InitiateTokenCallback;
